import { useState } from 'react';
import type { Video } from '@blackblack/shared';
import { api, errorMessage } from '../../lib/api';
import { navigate } from '../../lib/router';
import { invalidate, resetInfinite } from '../../lib/query';
import { useAuth } from '../../state/auth';
import { useToast } from '../../state/toast';
import { Icon } from '../../components/ui/Icon';
import { ActionList, Sheet } from '../../components/ui/Sheet';
import { ReportSheet } from '../moderation/ReportSheet';

export function VideoMenuSheet({ video, onClose }: { video: Video; onClose: () => void }) {
  const { me, requireAuth } = useAuth();
  const toast = useToast();
  const [reporting, setReporting] = useState(false);
  const isOwn = me?.id === video.author.id;

  if (reporting) return <ReportSheet targetType="video" targetId={video.id} onClose={onClose} />;

  return (
    <Sheet open onClose={onClose} title="Opties">
      <ActionList
        actions={[
          {
            label: 'Link kopiëren',
            icon: <Icon name="link" size={20} />,
            onClick: async () => {
              await navigator.clipboard?.writeText(`${location.origin}/v/${video.id}`).catch(() => {});
              toast('Link gekopieerd');
              onClose();
            },
          },
          {
            label: 'Dit geluid gebruiken',
            icon: <Icon name="note" size={20} />,
            hidden: !video.track,
            onClick: () => {
              onClose();
              if (requireAuth('Log in om video’s te maken')) navigate(`/create?track=${video.track!.id}`);
            },
          },
          {
            label: 'Niet geïnteresseerd',
            icon: <Icon name="ban" size={20} />,
            hidden: isOwn,
            onClick: () => {
              void api.post('/analytics/events', { events: [{ type: 'video_skip', entityId: video.id, value: 0, context: { reason: 'not_interested' } }] }).catch(() => {});
              toast('Je ziet minder van dit soort video’s');
              onClose();
            },
          },
          {
            label: 'Rapporteren',
            icon: <Icon name="flag" size={20} />,
            hidden: isOwn,
            danger: true,
            onClick: () => requireAuth('Log in om te rapporteren') && setReporting(true),
          },
          {
            label: 'Video verwijderen',
            icon: <Icon name="trash" size={20} />,
            hidden: !isOwn,
            danger: true,
            onClick: async () => {
              if (!confirm('Deze video definitief verwijderen?')) return;
              try {
                await api.delete(`/videos/${video.id}`);
                toast('Video verwijderd');
                resetInfinite('feed:');
                resetInfinite(`user-videos:${video.author.id}`);
                invalidate(`profile:`);
                onClose();
                navigate(`/u/${video.author.username}`, { replace: true });
              } catch (e) {
                toast(errorMessage(e), { tone: 'error' });
              }
            },
          },
        ]}
      />
    </Sheet>
  );
}
